/**
 * Validate a scene and solve it on its bench, headlessly — including a scene
 * that runs backwards, whose given is found by searching the forward engine.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * Every error is written to be handed back to the compiler as it stands: it
 * names the field and says what would be right, because the one repair a
 * runtime compile gets is only as good as the message it is given.
 */
import { benchOf } from './registry';
import { AskAnswer, BenchManifest, BenchSolution, Reading, Scene, SceneSolution } from './types';

type AnyBench = BenchManifest<never, unknown>;

const getPath = (o: Record<string, unknown>, path: string): unknown =>
    path.split('.').reduce<unknown>((v, k) => (v && typeof v === 'object' ? (v as Record<string, unknown>)[k] : undefined), o);

function setPath(o: Record<string, unknown>, path: string, value: number): Record<string, unknown> {
    const out = structuredClone(o);
    const keys = path.split('.');
    let at: Record<string, unknown> = out;
    for (const k of keys.slice(0, -1)) at = at[k] as Record<string, unknown>;
    at[keys[keys.length - 1]] = value;
    return out;
}

/** Everything wrong with a scene before anything is solved. Empty when it is good. */
export function validateScene(scene: Scene): string[] {
    if (!scene || typeof scene !== 'object') return ['scene must be an object'];
    const bench = benchOf(scene.bench);
    if (!bench) return [`bench must be one of the registered benches (got ${JSON.stringify(scene.bench)})`];
    const errors: string[] = [];
    if (scene.specVersion !== bench.specVersion) errors.push(`specVersion must be ${bench.specVersion} for the ${bench.bench} bench`);
    errors.push(...bench.validate(scene.setup).map(e => `setup: ${e}`));

    if (!Array.isArray(scene.asks) || scene.asks.length === 0) errors.push('asks must list at least one readout the question asks for');
    else {
        scene.asks.forEach((ask, i) => {
            const decl = bench.readouts.find(r => r.key === ask?.readout);
            if (!decl) errors.push(`asks[${i}].readout ${JSON.stringify(ask?.readout)} is not a readout of this bench; use one of ${bench.readouts.map(r => r.key).join(', ')}`);
            else if (ask.on !== undefined && !decl.on) errors.push(`asks[${i}].on is not accepted by ${decl.key}`);
        });
    }
    if (!Array.isArray(scene.assumptions)) errors.push('assumptions must be a list (it may be empty)');

    const inv = scene.inverse;
    if (inv) {
        if (typeof getPath(scene.setup ?? {}, inv.param) !== 'number') {
            errors.push(`inverse.param ${JSON.stringify(inv.param)} must name a number already in setup — give it a trial value`);
        }
        const decl = bench.readouts.find(r => r.key === inv.readout);
        if (!decl || decl.categories) errors.push(`inverse.readout ${JSON.stringify(inv.readout)} must be a numeric readout of this bench`);
        if (typeof inv.equals !== 'number' || !Number.isFinite(inv.equals)) errors.push('inverse.equals must be a number, in the readout\'s unit');
        if (!Array.isArray(inv.range) || inv.range.length !== 2 || !(inv.range[0] < inv.range[1])) errors.push('inverse.range must be [low, high] with low < high');
    }
    return errors;
}

const SAMPLES = 24;
const STEPS = 50;

/** The parameter value at which the readout equals the given, or why there is none. */
function invert(bench: AnyBench, scene: Scene): { setup: Record<string, unknown>; found: number } | { error: string } {
    const inv = scene.inverse!;
    const at = (x: number): { setup: Record<string, unknown>; value: number | null } => {
        const setup = setPath(scene.setup, inv.param, x);
        if (bench.validate(setup).length) return { setup, value: null };
        const r = bench.solve(setup as never);
        const v = r.readings[inv.readout];
        return { setup, value: r.problems.length || typeof v !== 'number' || !Number.isFinite(v) ? null : v };
    };

    const [lo, hi] = inv.range;
    const xs = Array.from({ length: SAMPLES + 1 }, (_, i) => lo + ((hi - lo) * i) / SAMPLES);
    const ys = xs.map(x => at(x).value);
    if (ys.some(y => y === null)) {
        return { error: `inverse: ${inv.readout} has no value somewhere in ${inv.param} ∈ [${lo}, ${hi}] — narrow the range to where the setup is valid` };
    }
    const d = (ys as number[]).slice(1).map((y, i) => y - (ys[i] as number));
    const rising = d.every(v => v > 0), falling = d.every(v => v < 0);
    if (!rising && !falling) {
        return { error: `inverse-not-monotonic: ${inv.readout} does not change one way only over ${inv.param} ∈ [${lo}, ${hi}], so it could have two answers — narrow the range (for a lens, keep to one side of the focal point)` };
    }
    const f = (y: number): number => (rising ? y - inv.equals : inv.equals - y);
    if (f(ys[0] as number) > 0 || f(ys[ys.length - 1] as number) < 0) {
        const [a, b] = [ys[0] as number, ys[ys.length - 1] as number].sort((p, q) => p - q);
        return { error: `inverse: ${inv.readout} = ${inv.equals} is not reached for ${inv.param} ∈ [${lo}, ${hi}] (it runs from ${+a.toPrecision(4)} to ${+b.toPrecision(4)})` };
    }
    let a = lo, b = hi;
    for (let i = 0; i < STEPS; i++) {
        const m = (a + b) / 2;
        const y = at(m).value as number;
        if (f(y) < 0) a = m; else b = m;
    }
    const found = (a + b) / 2;
    return { setup: setPath(scene.setup, inv.param, found), found };
}

/** Solve a scene on its bench and answer each ask. */
export function solveScene(scene: Scene): SceneSolution {
    const fail = (errors: string[]): SceneSolution => ({ ok: false, errors, answers: [], readings: {} });
    const errors = validateScene(scene);
    if (errors.length) return fail(errors);
    const bench = benchOf(scene.bench) as AnyBench;

    let setup = scene.setup;
    let found: number | undefined;
    if (scene.inverse) {
        const r = invert(bench, scene);
        if ('error' in r) return fail([r.error]);
        setup = r.setup;
        found = r.found;
    }

    let solved: BenchSolution;
    try {
        solved = bench.solve(setup as never);
    } catch (e) {
        return fail([`the ${bench.bench} engine failed on this setup: ${(e as Error).message}`]);
    }
    if (solved.problems.length) return fail(solved.problems.map(p => `the bench: ${p}`));

    const answers: AskAnswer[] = scene.asks.map(ask => {
        const decl = bench.readouts.find(r => r.key === ask.readout)!;
        const value: Reading = ask.on !== undefined
            ? solved.indexed?.[ask.readout]?.[ask.on] ?? null
            : solved.readings[ask.readout] ?? null;
        return { ask, value, ...(decl.unit !== undefined ? { unit: decl.unit } : {}) };
    });
    return {
        ok: true, errors: [], answers, readings: solved.readings,
        ...(solved.working ? { working: solved.working } : {}),
        ...(found !== undefined ? { found } : {}),
    };
}

/** The scene with unstated conventions filled in as the check found them: `{ g: 10 }`. */
export function withAssumed(scene: Scene, assumed?: Record<string, number>): Scene {
    if (!assumed || !Object.keys(assumed).length) return scene;
    const setup = Object.entries(assumed).reduce((s, [path, v]) => setPath(s, path, v), scene.setup);
    return { ...scene, setup };
}

/** The setup the bench should open on: an inverse scene's, with its parameter found. */
export function solvedSetup(scene: Scene, assumed?: Record<string, number>): Record<string, unknown> | null {
    const s = withAssumed(scene, assumed);
    if (!s.inverse) return s.setup;
    const r = solveScene(s);
    return r.ok && r.found !== undefined ? setPath(s.setup, s.inverse.param, r.found) : null;
}
